import * as React from "react";
import {
  Query,
  ResultData,
  SearchPillData,
  ResultHighlight,
  KeyValue,
} from "../../interfaces";
import { Grid, Typography } from "@mui/material";
import { AppBody } from "../AppBody";
import { useEffect } from "react";
import { apiPost, fetchData } from "../../common/services/ApiClient";

import BankFindResults from "../../containers/BankFindResults";
import LinkComponent from "../LinkComponent";
import {
  appHeaderTitle,
  appTitles,
  setInstitutionStatus,
} from "../../common/services/Utilities";
import { getBankFindApiFilters } from "../../common/services/BankFindUtilities";
import SearchPills from "../../containers/SearchPills";
import ShowHideButton from "../ShowHideButton";
import QuickTips from "./BankFindQuickTips";
import DataViz from "./BankFindDataViz";
import { ApiBaseUrl } from "../../config";
import { locationSearchPills } from "../../common/services/LocationUtilities";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import BankFindSearchForm from "../../containers/BankFindSearchForm";

interface Props {
  appState: Query;
  onStartRequest: () => void;
  onResultSuccess: () => void;
  onResultFailure: (error: Error) => void;
  updateUi: (uiField: string, uiValue: string | undefined) => void;
  resetState: () => void;
  dataLoaded: boolean;
  updateFilter: (field: string, value: string | undefined) => void;
}

type FIBResultData = {
  inactive: ResultData;
  active: ResultData;
};

type FIBCounts = {
  inactive: number;
  active: number;
};

interface BankFindAppState {
  resultData: FIBResultData;
  createTimestamp: string;
  recordCount: FIBCounts;
  resultHighlight: ResultHighlight;
}

const BankfindIntro = (
  <>
    {appHeaderTitle("bankFind")}
    <Typography variant="body2">
      Find Insured Banks allows you to find FDIC-insured banks and branches from
      today, to last year, and all the way back to 1934. All search fields are
      optional so be as general or as specific as you need to be.
    </Typography>
    <LinkComponent appName={"NameLocation"} />
  </>
);

export default function BankFind({
  appState,
  onStartRequest,
  onResultSuccess,
  onResultFailure,
  updateUi,
  resetState,
  dataLoaded,
  updateFilter,
}: Props) {
  const [locationValues, setLocationValues] = React.useState("");
  const [currentState, setCurrentState] = React.useState(appState);
  const [prevState, setPrevState] = React.useState(appState);
  const [bankFindAppState, setBankFindAppState] =
    React.useState<BankFindAppState>({
      resultData: { active: [], inactive: [] },
      createTimestamp: "",
      recordCount: { active: 0, inactive: 0 },
      resultHighlight: [],
    });
  const [dataDownloadPost, setDataDownloadPost] = React.useState<{
    active: KeyValue;
    inactive: KeyValue;
  }>({ active: {}, inactive: {} });
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<"Active" | "Inactive">(
    "Active",
  );

  useEffect(() => {
    if (initialLoad) {
      if (appState.filters.stalpbr !== undefined) {
        updateFilter("stalpbr", "");
      }
      if (appState.filters.county !== undefined) {
        updateFilter("county", "");
      }
      setInitialLoad(false);
    }
  }, [
    appState.filters.county,
    appState.filters.stalpbr,
    initialLoad,
    updateFilter,
    updateUi,
  ]);

  const locationFormOptions =
    appState.filters.stalp ||
    appState.filters.zip ||
    appState.search.city ||
    appState.filters.countyInst;

  const showBankFindResults =
    Object.keys(appState.filters).some(
      (key: string) => appState.filters[key],
    ) ||
    Object.keys(appState.search).some((key: string) => appState.search[key]);

  const bankfindSearchPills: SearchPillData[] = [
    {
      value: locationFormOptions
        ? appState.ui.branchOffices
          ? "Include Branch Offices"
          : "Main Office Only"
        : "",
      static: true,
      search: false,
      formKey: "branchOffices",
    },
    {
      label: "Name",
      value: appState.search.name,
      search: true,
      formKey: "name",
    },

    {
      label: "Cert",
      value: appState.filters.cert,
      search: false,
      formKey: "cert",
    },
    {
      label: "County",
      value: appState.filters.countyInst,
      search: false,
      formKey: "countyInst",
    },
    {
      label: "URL",
      value: appState.search.url,
      search: true,
      formKey: "url",
    },
    ...locationSearchPills(appState),
  ];

  const searchPills = (
    <div id="bankfind-search-pills-container">
      <ShowHideButton
        title="Selected Search Criteria"
        body={<SearchPills dataArray={bankfindSearchPills} />}
      />
    </div>
  );

  useEffect(() => {
    setCurrentState({
      ...appState,
      ui: {
        branchOffices: appState.ui.branchOffices,
      },
      urls: {},
    });
  }, [appState]);

  useEffect(() => {
    // used to set location filters for View Offices Modal
    const locationFilters = [];
    const locationSearch = [];
    if (appState.filters.zip) {
      locationFilters.push(`ZIP:"${appState.filters.zip}"`);
    }
    if (appState.filters.stalp) {
      locationFilters.push(`STALP:"${appState.filters.stalp}"`);
    }
    if (appState.search.city) {
      locationSearch.push(`CITY:${appState.search.city}`);
    }
    if (appState.ui.branchOffices) {
      setLocationValues(
        `${
          locationFilters.length > 0
            ? ` AND ${locationFilters.join(" AND ")}`
            : ""
        }${
          locationSearch.length > 0
            ? `&search=${locationSearch.join(" AND ")}`
            : ""
        }`,
      );
    } else {
      setLocationValues("");
    }
  }, [
    appState.filters.stalp,
    appState.filters.zip,
    appState.search.city,
    appState.ui.branchOffices,
  ]);

  useEffect(() => {
    // resets state to default when navigating to URL with no query params
    if (!window.location.search) {
      resetState();
    }
  }, [resetState]);

  useEffect(() => {
    // remove this from ui so the value is not appended to the URL on level 1
    if (appState.ui.bankfindLevelThreeView) {
      updateUi("bankfindLevelThreeView", undefined);
    }
  }, [appState.ui.bankfindLevelThreeView, updateUi]);

  const { sortField, sortOrder } = appState.sort ?? {};
  const sortByField = React.useCallback(
    <T extends Record<string, any>>(items: T[]): T[] => {
      return items.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];

        let comparison: number;
        if (typeof aVal === "string" && typeof bVal === "string") {
          comparison = aVal.localeCompare(bVal);
        } else {
          comparison = aVal < bVal ? 1 : aVal < bVal ? -1 : 0;
        }

        return sortOrder === "ASC" ? -comparison : comparison;
      });
    },
    [sortField, sortOrder],
  );

  useEffect(() => {
    // prevents the API from being called when UI updates are made that do not change the API request.
    if (
      JSON.stringify(currentState) !== JSON.stringify(prevState) &&
      appState.urls.browserPath
    ) {
      setPrevState(currentState);
      let pendingBanks: any = appState.ui.pendingClosedBanks;
      (async () => {
        onStartRequest();
        const activeBanksAppState = JSON.parse(JSON.stringify(appState));
        const inactiveBanksAppState = JSON.parse(JSON.stringify(appState));
        activeBanksAppState.filters.activeStatus = ["1"];
        inactiveBanksAppState.filters.activeStatus = ["0"];

        const activeApiFilters: any =
          await getBankFindApiFilters(activeBanksAppState);
        const inctiveApiFilters: any = await getBankFindApiFilters(
          inactiveBanksAppState,
        );
        const yearApiFilters: any = await getBankFindApiFilters(appState);

        const activeLocationsPostReq = apiPost(activeApiFilters);
        const inactiveLocationsPostReq = apiPost(inctiveApiFilters);
        const yearLocationsPostReq = apiPost(yearApiFilters);

        setDataDownloadPost({
          active: typeof activeApiFilters !== "string" ? activeApiFilters : {},
          inactive:
            typeof inctiveApiFilters !== "string" ? inctiveApiFilters : {},
        });

        const activeFetchApi =
          typeof activeApiFilters === "string"
            ? fetchData(activeApiFilters)
            : fetchData(`${ApiBaseUrl()}/institutions`, activeLocationsPostReq);

        const inactiveFetchApi =
          typeof inctiveApiFilters === "string"
            ? fetchData(inctiveApiFilters)
            : fetchData(
                `${ApiBaseUrl()}/institutions`,
                inactiveLocationsPostReq,
              );

        const yearFetchApi =
          typeof yearApiFilters === "string"
            ? fetchData(yearApiFilters)
            : fetchData(`${ApiBaseUrl()}/institutions`, yearLocationsPostReq);

        await Promise.all([activeFetchApi, inactiveFetchApi, yearFetchApi])
          .then((response) => {
            const pendingCerts = new Set(
              pendingBanks?.map((bank: { CERT: string }) => Number(bank.CERT)),
            );

            let activeInts = response[0].data.map((data: any) =>
              setInstitutionStatus(data, pendingCerts.has(data.CERT)),
            );
            let inactiveInts = response[1].data.map((data: any) => {
              // if the closed bank DOES have failure data, remove it from appState
              if (pendingCerts.has(data.CERT)) {
                updateUi(
                  "pendingClosedBanks",
                  pendingBanks.filter(
                    (bank: { CERT: string }) => Number(bank.CERT) !== data.CERT,
                  ),
                );
              }
              return setInstitutionStatus(data, false);
            });

            // if there are any recently closed banks, we need to move them into Inactive
            // only run this code if a closed bank exists in the activeInts search results
            if (
              pendingBanks &&
              activeInts.some((c: any) => pendingCerts.has(c.CERT))
            ) {
              let i = 0;
              while (i < activeInts.length) {
                if (pendingCerts.has(activeInts[i].CERT)) {
                  inactiveInts.push(activeInts.splice(i, 1)[0]);
                  response[0].totals.count--;
                  response[1].totals.count++;
                } else {
                  i++;
                }
              }
              // re-sort results
              if (sortField !== "" && sortOrder !== "") {
                activeInts = sortByField(activeInts);
                inactiveInts = sortByField(inactiveInts);
              }
            }

            // if no Active results, show Inactive tab
            if (response[0].totals.count === 0) {
              setActiveTab("Inactive");
            }

            setBankFindAppState({
              resultData: {
                active: activeInts,
                inactive: inactiveInts,
              },
              recordCount: {
                active: response[0].totals.count,
                inactive: response[1].totals.count,
              },
              createTimestamp: response[0].createTimestamp,
              resultHighlight: response[0].highlight,
            });

            if (response[2].data?.[0]?.RISDATE) {
              updateUi("year", response[2].data[0].RISDATE.slice(6));
            }

            onResultSuccess();
          })
          .catch((error: Error) => {
            onResultFailure(error);
          });
      })();
    }
  }, [
    appState,
    appState.ui.pendingClosedBanks,
    currentState,
    onResultFailure,
    onResultSuccess,
    onStartRequest,
    prevState,
    sortByField,
    sortField,
    sortOrder,
    updateUi,
  ]);

  useInitialPageLoad("bankFind");
  const resultsToShow =
    activeTab === "Active"
      ? bankFindAppState.resultData.active
      : bankFindAppState.resultData.inactive;

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle={appTitles("bankFind")} />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        introText={BankfindIntro}
        searchForm={<BankFindSearchForm />}
        dataViz={!showBankFindResults ? <DataViz /> : null}
      >
        {showBankFindResults ? (
          <BankFindResults
            dataDownloadPost={
              activeTab === "Active"
                ? dataDownloadPost.active
                : dataDownloadPost.inactive
            }
            searchPills={searchPills}
            resultData={resultsToShow}
            recordCount={bankFindAppState.recordCount}
            resultHighlight={bankFindAppState.resultHighlight}
            dataLoaded={dataLoaded}
            locationValues={locationValues}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        ) : (
          <QuickTips />
        )}
      </AppBody>
    </>
  );
}
